import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import { FetchParams } from './employeesSlice';
import { buildQueryString } from '../../utils/queryStringBuilder';

interface MeasurementsState {
    measurements: any[];
    measurement: any | null;
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        pageSize: number;
        total: number;
    };
}

const initialState: MeasurementsState = {
    measurements: [],
    measurement: null,
    isLoading: false,
    error: null,
    pagination: {
        page: 0,
        pageSize: 10,
        total: 0
    }
};

export const fetchMeasurements = createAsyncThunk(
    'measurements/fetchMeasurements',
    async (params: FetchParams = {}, { rejectWithValue }) => {
        try {
            // Build query string using the utility function
            const queryString = buildQueryString(params);
            
            const response = await api.get(`/api/measurements${queryString}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch measurements');
        }
    }
);

export const fetchMeasurement = createAsyncThunk(
    'measurements/fetchMeasurement',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/measurements/${id}?populate=*`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to fetch measurement');
        }
    }
);

export const createMeasurement = createAsyncThunk(
    'measurements/createMeasurement',
    async (measurementData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/measurements', { data: measurementData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to create measurement');
        }
    }
);

export const updateMeasurement = createAsyncThunk(
    'measurements/updateMeasurement',
    async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/api/measurements/${id}`, { data });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to update measurement');
        }
    }
);

export const deleteMeasurement = createAsyncThunk(
    'measurements/deleteMeasurement',
    async (id: number, { rejectWithValue }) => {
        try {
            await api.delete(`/api/measurements/${id}`);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.error?.message || 'Failed to delete measurement');
        }
    }
);

const measurementsSlice = createSlice({
    name: 'measurements',
    initialState,
    reducers: {
        clearMeasurementError: (state) => {
            state.error = null;
        },
        clearMeasurement: (state) => {
            state.measurement = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch Measurements
            .addCase(fetchMeasurements.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchMeasurements.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.measurements = action.payload.data;
                if (action.payload.meta && action.payload.meta.pagination) {
                    state.pagination = {
                        page: action.payload.meta.pagination.page - 1, // Convert to 0-based for frontend
                        pageSize: action.payload.meta.pagination.pageSize,
                        total: action.payload.meta.pagination.total
                    };
                }
            })
            .addCase(fetchMeasurements.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Fetch Measurement
            .addCase(fetchMeasurement.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchMeasurement.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.measurement = action.payload.data;
            })
            .addCase(fetchMeasurement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Create Measurement
            .addCase(createMeasurement.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createMeasurement.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                state.measurements.push(action.payload.data);
            })
            .addCase(createMeasurement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Update Measurement
            .addCase(updateMeasurement.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateMeasurement.fulfilled, (state, action: PayloadAction<any>) => {
                state.isLoading = false;
                const index = state.measurements.findIndex(measurement => measurement.id === action.payload.data.id);
                if (index !== -1) {
                    state.measurements[index] = action.payload.data;
                }
                state.measurement = action.payload.data;
            })
            .addCase(updateMeasurement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            // Delete Measurement
            .addCase(deleteMeasurement.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteMeasurement.fulfilled, (state, action: PayloadAction<number>) => {
                state.isLoading = false;
                state.measurements = state.measurements.filter(measurement => measurement.id !== action.payload);
                if (state.measurement && state.measurement.id === action.payload) {
                    state.measurement = null;
                }
            })
            .addCase(deleteMeasurement.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearMeasurementError, clearMeasurement } = measurementsSlice.actions;
export default measurementsSlice.reducer; 